import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Tour, { TOUR_KEY } from "./Tour";

/**
 * App-level mount point for the Rundtour. Lives OUTSIDE the route hierarchy
 * so Tour state survives navigation (the Tour component itself triggers
 * `useNavigate` between steps — if Tour lived inside AppShell, each route
 * change would unmount it).
 */
export default function TourHost() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  // Auto-open on the first visit — but only once the user is somewhere the
  // tour can actually guide: /login and /setup have no app UI, so we wait
  // there and re-check on every route change (setup completion navigates
  // client-side to "/", which used to slip past a mount-only check and the
  // tour never fired). Also: ?tour=1 in the URL forces the tour open (set by
  // BillingSuccessPage right after Stripe).
  useEffect(() => {
    if (typeof window === "undefined" || open) return;
    const params = new URLSearchParams(window.location.search);
    const forced = params.get("tour") === "1";
    if (forced) {
      window.localStorage.removeItem(TOUR_KEY);
    } else {
      if (window.localStorage.getItem(TOUR_KEY)) return;
      if (loc.pathname.startsWith("/login") || loc.pathname.startsWith("/setup")) return;
    }
    // 600ms grace: if this route immediately redirects (e.g. "/" → /setup
    // for a household-less user), the cleanup cancels the timer.
    const t = setTimeout(() => setOpen(true), 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname, open]);

  // Allow other components to ask the tour to open by dispatching a window
  // event — cheaper than threading a context through AppShell.
  useEffect(() => {
    function handler() {
      window.localStorage.removeItem(TOUR_KEY);
      setOpen(true);
    }
    window.addEventListener("logbuch:open-tour", handler);
    return () => window.removeEventListener("logbuch:open-tour", handler);
  }, []);

  if (!open) return null;
  return <Tour onClose={() => setOpen(false)} />;
}
